import { parse, differenceInSeconds } from "date-fns";
import * as XLSX from "xlsx";
import { BaseFermentationParser, } from "./parser-interface";
/**
 * Applikon parser implementing the FermentationTimeseriesParser interface
 */
export class ApplikonParser extends BaseFermentationParser {
    equipmentType = "Applikon";
    supportedFormats = [".xlsx", ".xls"];
    argsSchema = {
        enablePumpCalibration: {
            type: "boolean",
            required: false,
            description: "Enable methanol pump calibration using linear equation",
        },
        pumpSlope: {
            type: "number",
            required: false,
            description: "Pump calibration slope (a in y=ax+b)",
        },
        pumpOffset: {
            type: "number",
            required: false,
            description: "Pump calibration offset (b in y=ax+b)",
        },
        startingWeight: {
            type: "number",
            required: false,
            description: "Starting weight for pump calibration (kg)",
        },
        convertPressure: {
            type: "boolean",
            required: false,
            description: "Convert pressure from delta bar to atm",
        },
    };
    workbook;
    worksheet;
    hasTimeHours;
    hasTimestamp;
    constructor() {
        super();
    }
    decodeFile(buffer) {
        // Excel files don't need text decoding, return as-is
        return buffer;
    }
    async validateFile(buffer) {
        try {
            const workbook = XLSX.read(buffer);
            const errors = [];
            const warnings = [];
            // Check if workbook has sheets
            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                errors.push("No worksheets found in Excel file");
                return { isValid: false, errors };
            }
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!worksheet) {
                errors.push("First worksheet is empty or cannot be read");
                return { isValid: false, errors };
            }
            // Check time format
            const firstRow = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
            })[0];
            if (!firstRow || firstRow.length === 0) {
                errors.push("First row is empty - cannot determine column structure");
                return { isValid: false, errors };
            }
            const hasTimeHours = firstRow.some((header) => header === "Time [h]");
            const hasTimestamp = firstRow.some((header) => header.toLowerCase() === "timestamp" ||
                header.toLowerCase() === "time stamp" ||
                header.toLowerCase() === "date time" ||
                header.toLowerCase() === "datetime");
            if (!hasTimeHours && !hasTimestamp) {
                errors.push("No valid time column found ('Time [h]' or 'Timestamp' expected)");
            }
            // Check for some expected columns
            const expectedColumns = [
                "m_stirrer",
                "sp_stirrer",
                "m_ph_1",
                "sp_temp",
                "M_OD",
                "m_pressure",
            ];
            const missingColumns = expectedColumns.filter((col) => !firstRow.some((header) => header === col));
            if (missingColumns.length > 2) {
                warnings.push(`Some expected columns missing: ${missingColumns.join(", ")}`);
            }
            return {
                isValid: errors.length === 0,
                errors,
                warnings,
            };
        }
        catch (error) {
            return {
                isValid: false,
                errors: [`File validation failed: ${error}`],
            };
        }
    }
    async extractMetadata(buffer) {
        this.initializeWorkbook(buffer);
        const rows = XLSX.utils.sheet_to_json(this.worksheet);
        let startTime;
        let endTime;
        let durationSeconds;
        if (this.hasTimeHours) {
            // Relative time only — no absolute start/end is recorded in the file.
            let maxHours = null;
            for (const row of rows) {
                const hours = this.toNumber(this.getVal(row, "Time [h]"));
                if (hours === null)
                    continue;
                if (maxHours === null || hours > maxHours)
                    maxHours = hours;
            }
            if (maxHours !== null)
                durationSeconds = maxHours * 3600;
        }
        else {
            // Timestamp format carries absolute times: first and last parseable rows.
            for (const row of rows) {
                const timestamp = this.getVal(row, "Timestamp");
                if (typeof timestamp !== "string")
                    continue;
                const date = this.parseTimestamp(timestamp);
                if (date) {
                    startTime = date;
                    break;
                }
            }
            for (let i = rows.length - 1; i >= 0; i--) {
                const timestamp = this.getVal(rows[i], "Timestamp");
                if (typeof timestamp !== "string")
                    continue;
                const date = this.parseTimestamp(timestamp);
                if (date) {
                    endTime = date;
                    break;
                }
            }
            if (startTime && endTime) {
                durationSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
            }
        }
        return {
            equipmentType: this.equipmentType,
            startTime,
            endTime,
            durationSeconds,
            tankNumbers: [0], // Applikon files only contain one tank
        };
    }
    async parseData(buffer, fermentations, _tankIdToFermIdMap, args, additionTotals) {
        this.initializeWorkbook(buffer);
        const ferm = fermentations[0];
        if (!ferm.started_at) {
            throw new Error("Fermentation start time is required");
        }
        const totals = additionTotals?.[ferm.id];
        const fermentationInfo = {
            start_time: ferm.started_at,
            antifoam_total_g: totals?.antifoam_total_g ?? 0,
            base_total_g: totals?.base_total_g ?? 0,
            methanol_total_g: totals?.methanol_total_g ?? 0,
        };
        const records = [];
        const rows = XLSX.utils.sheet_to_json(this.worksheet);
        // Get the maximum pump values to calculate ratios
        const maxAntifoam = Math.max(...rows.map((row) => this.toNumber(this.getVal(row, "dm_pump_antifoam")) ?? 0));
        const maxBase = Math.max(...rows.map((row) => this.toNumber(this.getVal(row, "dm_pump_base")) ?? 0));
        const maxMethanol = Math.max(...rows.map((row) => this.toNumber(this.getVal(row, "dm_pump_methanol")) ?? 0));
        for (const row of rows) {
            // Calculate EFT based on available time format
            let timestamp_s;
            if (this.hasTimeHours) {
                const timeHours = this.toNumber(this.getVal(row, "Time [h]"));
                if (timeHours === null)
                    continue;
                timestamp_s = timeHours * 3600;
            }
            else {
                const timestamp = this.getVal(row, "Timestamp");
                if (typeof timestamp !== "string")
                    continue;
                const date = this.parseTimestamp(timestamp);
                if (!date)
                    continue;
                timestamp_s = differenceInSeconds(date, fermentationInfo.start_time);
            }
            // Create raw data object for mapping
            const rawData = this.createRawData(row, fermentationInfo, {
                maxAntifoam,
                maxBase,
                maxMethanol,
            }, args);
            // Map attributes to standardized format
            const attributes = this.mapAttributes(rawData);
            records.push({
                timestamp_s,
                attributes,
            });
        }
        return [
            {
                fermentationId: ferm.id,
                records,
            },
        ];
    }
    /**
     * Applikon exports the same quantity under two columns in three places, so
     * order matters: the entry that should win is written last, because
     * `mapAttributes` writes in declaration order and only for columns the file
     * actually contains.
     *
     * `pressure_atm` and `pressure_value` are the same reading in different
     * units, converted by `getAttributeScaling` below. `*_added_total_g` is the
     * measured (scale-derived) total of a line and `*_total_g` the software
     * total; both are the line's cumulative amount and belong to one series,
     * with the software total preferred where the file carries both.
     */
    getAttributeMapping() {
        return {
            // pH attributes
            ph_value: "ph_pv",
            ph_setpoint: "ph_sp",
            ph_output_pct: "ph_out",
            // Temperature attributes
            temperature_value: "temp_pv",
            temperature_setpoint: "temp_sp",
            temperature_output_pct: "temp_out",
            // Dissolved oxygen attributes
            do_value_pct: "do_pv",
            do_setpoint_pct: "do_sp",
            // Agitation attributes
            agitation_value_rpm: "agit_pv",
            agitation_setpoint_rpm: "agit_sp",
            // Air flow attributes
            air_flow_value_l_min: "airflow_pv",
            air_flow_setpoint_l_min: "airflow_sp",
            // Line totals, measured first so the software total wins where both exist.
            methanol_added_total_g: "methanol_feed_total",
            antifoam_added_total_g: "antifoam_feed_total",
            antifoam_total_g: "antifoam_feed_total",
            base_total_g: "base_feed_total",
            methanol_total_g: "methanol_feed_total",
            // Additional Applikon-specific attributes
            optical_density_value: "od",
            pressure_atm: "pressure_pv",
            pressure_value: "pressure_pv",
            pressure_output_pct: "pressure_out",
            methanol_scale_value: "methanol_feed_scale",
            antifoam_scale_value: "antifoam_feed_scale",
            broth_weight_kg: "broth_weight_pv",
            methanol_pump_setpoint_pct: "methanol_feed_pump_out",
            foam_value: "foam_pv",
            // `antifoam_target_weight_g` is deliberately absent: it is the weight the
            // dosing loop was aiming at, not a measurement of anything, and has no
            // attribute in the standard.
        };
    }
    /** atm to bar, the unit `pressure_pv` is registered in. */
    getAttributeScaling() {
        return { pressure_atm: 1.01325 };
    }
    initializeWorkbook(buffer) {
        this.workbook = XLSX.read(buffer);
        this.worksheet = this.workbook.Sheets[this.workbook.SheetNames[0]];
        if (!this.worksheet) {
            throw new Error("Invalid Applikon file - no worksheet found");
        }
        // Detect time format
        const firstRow = XLSX.utils.sheet_to_json(this.worksheet, {
            header: 1,
        })[0];
        this.hasTimeHours = firstRow.some((header) => header === "Time [h]");
        this.hasTimestamp = firstRow.some((header) => header.toLowerCase() === "timestamp" ||
            header.toLowerCase() === "time stamp" ||
            header.toLowerCase() === "date time" ||
            header.toLowerCase() === "datetime");
        if (!this.hasTimeHours && !this.hasTimestamp) {
            throw new Error("No valid time column found in Applikon file");
        }
    }
    createRawData(row, fermentationInfo, maxValues, args) {
        // Calculate calibrated pump totals
        const antifoamRaw = this.toNumber(this.getVal(row, "dm_pump_antifoam"));
        const baseRaw = this.toNumber(this.getVal(row, "dm_pump_base"));
        const methanolRaw = this.toNumber(this.getVal(row, "dm_pump_methanol"));
        const antifoam_total_g = antifoamRaw !== null && maxValues.maxAntifoam > 0
            ? (antifoamRaw / maxValues.maxAntifoam) *
                fermentationInfo.antifoam_total_g
            : null;
        const base_total_g = baseRaw !== null && maxValues.maxBase > 0
            ? (baseRaw / maxValues.maxBase) * fermentationInfo.base_total_g
            : null;
        const methanol_total_g = methanolRaw !== null && maxValues.maxMethanol > 0
            ? (methanolRaw / maxValues.maxMethanol) *
                fermentationInfo.methanol_total_g
            : null;
        // Get raw methanol pump value
        let methanolPumpValue = this.toNumber(this.getVal(row, "varpump1_spmanual"));
        // Apply pump calibration if enabled
        if (args?.enablePumpCalibration && methanolPumpValue !== null) {
            const slope = args.pumpSlope || 16.2; // Default from Python script
            const offset = args.pumpOffset || -16.1; // Default from Python script
            const startingWeight = args.startingWeight || 150; // Default from Python script
            // Apply calibration: y = a*x + b, then normalize by starting weight
            methanolPumpValue = (methanolPumpValue * slope + offset) / startingWeight;
        }
        // Get raw pressure value
        const rawPressure = this.toNumber(this.getVal(row, "m_pressure"));
        let pressureAtm = null;
        // Convert pressure from delta bar to atm if enabled
        if (args?.convertPressure && rawPressure !== null) {
            // Pressure from delta bar to atm: (delta_bar + 1.01325) * 0.986923
            pressureAtm = (rawPressure + 1.01325) * 0.986923;
        }
        return {
            // Map available values with averaging where needed
            do_value_pct: this.average(this.toNumber(this.getVal(row, "m_do_1")), this.toNumber(this.getVal(row, "m_do_2"))),
            do_setpoint_pct: this.toNumber(this.getVal(row, "sp_do")),
            ph_value: this.average(this.toNumber(this.getVal(row, "m_ph_1")), this.toNumber(this.getVal(row, "m_ph_2"))),
            ph_setpoint: this.toNumber(this.getVal(row, "sp_ph")),
            ph_output_pct: this.toNumber(this.getVal(row, "ctrl_out_ph")),
            air_flow_value_l_min: this.toNumber(this.getVal(row, "m_sparger_air")),
            air_flow_setpoint_l_min: this.toNumber(this.getVal(row, "sp_sparger_air")),
            agitation_value_rpm: this.toNumber(this.getVal(row, "m_stirrer")),
            agitation_setpoint_rpm: this.toNumber(this.getVal(row, "sp_stirrer")),
            temperature_value: this.average(this.toNumber(this.getVal(row, "m_temp1_TE2.01")), this.toNumber(this.getVal(row, "m_temp2_TE2.02"))),
            temperature_setpoint: this.toNumber(this.getVal(row, "sp_temp")),
            temperature_output_pct: this.toNumber(this.getVal(row, "ctrl_out_temp")),
            // Calibrated totals
            antifoam_total_g,
            base_total_g,
            methanol_total_g,
            // Additional Applikon-specific fields
            optical_density_value: this.toNumber(this.getVal(row, "M_OD")),
            pressure_value: rawPressure,
            pressure_atm: pressureAtm,
            methanol_scale_value: this.toNumber(this.getVal(row, "m_scale1")),
            antifoam_scale_value: this.toNumber(this.getVal(row, "m_scale2")),
            broth_weight_kg: this.toNumber(this.getVal(row, "m_weight")),
            methanol_pump_setpoint_pct: methanolPumpValue,
            methanol_added_total_g: this.toNumber(this.getVal(row, "c_added_scale1")),
            antifoam_added_total_g: this.toNumber(this.getVal(row, "c_added_scale2")),
            antifoam_target_weight_g: this.toNumber(this.getVal(row, "c_AF_target_weight")),
            pressure_output_pct: this.toNumber(this.getVal(row, "ctrl_out_pressure")),
            foam_value: this.toNumber(this.getVal(row, "m_foam")),
        };
    }
    getVal(row, columnName) {
        const exactMatch = row[columnName];
        if (exactMatch !== undefined)
            return exactMatch;
        // Try case-insensitive match for timestamp column which might vary
        if (columnName === "Timestamp") {
            const key = Object.keys(row).find((k) => k.toLowerCase() === "timestamp" ||
                k.toLowerCase() === "time stamp" ||
                k.toLowerCase() === "date time" ||
                k.toLowerCase() === "datetime");
            return key ? row[key] : null;
        }
        return null;
    }
    average(...values) {
        const validValues = values.filter((v) => v !== null);
        if (validValues.length === 0)
            return null;
        return validValues.reduce((a, b) => a + b, 0) / validValues.length;
    }
    parseTimestamp(timestamp) {
        // Try common date formats
        const formats = [
            "dd-MM-yyyy HH:mm:ss",
            "yyyy-MM-dd HH:mm:ss",
            "yyyy-MM-dd'T'HH:mm:ss",
            "MM/dd/yyyy HH:mm:ss",
            "yyyy.MM.dd HH:mm:ss",
        ];
        for (const format of formats) {
            try {
                const date = parse(timestamp, format, new Date());
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }
            catch {
                continue;
            }
        }
        return null;
    }
}
//# sourceMappingURL=applikon.js.map